/** @format */

import database from '../../firebase/config';
import { observable, action, computed } from 'mobx';
import { getTimestamp } from '../../helpers/getTimestamp';
import { getAttributeFromObject } from '../../helpers/objectHelpers';

export default class ResourceStore {
  @observable ref = `resources`;
  @observable items = {};
  @observable currentItem = {};
  @observable edit = { modal: false, currentItem: {}, errors: [] };
  @observable defaultItem = {};
  @observable tableKeys = [{ label: '', prop: '', order: 'asc' }];
  @observable filter = {
    status: 'all',
    rules: {
      all: { label: 'Tutti', name: 'all' },
    },
  };
  @observable search = {
    value: '',
    attributes: [],
  };

  constructor() {
    this.resetCurrentItem();
  }

  /**
   * Resetta la risorsa attualmente in modifica
   */
  @action resetCurrentItem = () => {
    this.edit.errors = false;
    this.edit.currentItem = { ...this.defaultItem };
  };

  /**
   * Setta la risorsa con id uguale a uid come risorsa in modifica
   * @param {String} uid
   */
  @action loadCurrentItem = uid => {
    this.edit.currentItem = this.items[uid];
  };

  /**
   * Cambia lo stato del modal di modifica
   */
  @action toggleEditModal = () => {
    this.edit.modal = !this.edit.modal;
    this.resetCurrentItem();
  };

  /**
   * Gestione del change dell'input
   * @param {any} value
   * @param {String} name
   */
  @action handleInputChange = (value, name) => {
    this.edit.currentItem[name] = value;
  };

  /**
   * Setta l'attributo items dell'oggetto con la lista degli utenti
   */
  @action fetch = () => {
    const query = database.ref(this.ref);
    query.off('value');
    query.once('value', snapshot => {
      const items = snapshot.val();
      const keys = Object.keys(items);
      for (let uid of keys) {
        const item = { ...items[uid], uid: uid };
        this.items[uid] = item;
      }

      if (this.ref === "warehouses") {
        console.log("Warehouses fetched");
        console.log(JSON.stringify(this.items));
      }
    }, (error) => {
      if (this.ref === "warehouses") {
        console.error("Error fetching warehouses:", error);
      }
    });
  };

  /**
   * Action per il submit dell'update o della create
   */
  @action submit = () => {
    let currentItem = this.edit.currentItem;
    currentItem.uid = this.edit.currentItem.uid
      ? this.edit.currentItem.uid
      : '';
    currentItem.timestamp = getTimestamp();

    const method = currentItem.uid !== '' ? 'set' : 'push';
    const userRef = database.ref(`${this.ref}/${currentItem.uid}`);
    userRef[method](currentItem)
      .then(item => {
        if (item && method === 'push') {
          currentItem.uid = item.key;
        }
        // Carico in lista la versione aggiornata dell'item (se update) oppure il nuovo item (se create)
        this.items[currentItem.uid] = currentItem;

        this.toggleEditModal();
        this.resetCurrentItem();
      })
      .catch(() => {
        this.edit.errors = true;
      });
  };

  /**
   * Rimuove l'elemento specificato
   * @param {String|Number} uid
   */
  @action deleteItem = uid => {
    database.ref(`${this.ref}/${uid}`).remove(response => {
      delete this.items[uid];

      this.toggleEditModal();
      this.resetCurrentItem();
    });
  };

  /**
   * Setta la modalità di filtraggio
   * @param {String} key
   */
  @action filterItems = key => {
    this.filter.status = key;
  };

  /**
   * Setta la keyword per la ricerca
   * @param {String} value
   */
  @action searchItem = value => {
    this.search.value = value;
  };

  /**
   * Restituisce gli elementi ordinati in base alla proprietà passata come parametro e nell'ordine order
   * @param {String} prop
   * @param {String} order
   */
  @action sortItemsBy(prop, order = 'asc') {
    const keys = Object.keys(this.items);

    keys.sort((first, second) => {
      if (order !== 'asc') {
        const tmp = first;
        first = second;
        second = tmp;
      }

      const firstValue = getAttributeFromObject(prop, this.items[first]);
      const secondValue = getAttributeFromObject(prop, this.items[second]);

      if (Number.isInteger(firstValue)) {
        return secondValue - firstValue;
      }
      return firstValue.localeCompare(secondValue);
    });

    this.tableKeys = this.tableKeys.map(item => {
      if (item.prop === prop) {
        item.order = item.order === 'asc' ? 'desc' : 'asc';
      }

      return item;
    });

    let items = {};
    for (let key of keys) {
      items[key] = this.items[key];
    }

    this.items = items;
  }

  /**
   * Restituisce gli items in formato array
   * @return {Array}
   */
  @computed get getItems() {
    return Object.values(this.items);
  }

  /**
   * Restituisce il numero degli item
   */
  @computed get itemCount() {
    return this.getItems.length;
  }

  /**
   * Restituisce gli item filtrati in base all'attributo filter
   * @return {Array<Object>}
   */
  @computed get filteredItems() {
    const items = this.getItems;
    const rule = this.filter.rules[this.filter.status];

    if (this.filter.status === 'all') {
      return items;
    }

    return items.filter(item => {
      return getAttributeFromObject(rule.name, item) === rule.value;
    });
  }

  /**
   * Restituisce gli item filtrati in base alla ricerca
   * @return {Array<Object>}
   */
  @computed get searchedItems() {
    const filteredItems = this.filteredItems;
    const searched = this.search.value.toLowerCase();

    if (searched === '' || searched.length < 3) {
      return filteredItems;
    }

    return filteredItems.filter(item => {
      for (let attribute of this.search.attributes) {
        const value = getAttributeFromObject(attribute, item)
          .toString()
          .toLowerCase();
        if (value.indexOf(searched) >= 0) {
          return true;
        }
      }
      return false;
    });
  }
}
